import $ from "jquery";
import React, { useEffect, useState, useRef } from "react";
import { Col, Nav, Navbar, Row } from "react-bootstrap";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../components/axiosInstance";
import { clear_session } from "../redux";
import logo from "../static/images/storlytics_white.png";
import CountdownTimer from "./CountdownTimer";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Logout } from "./Logout";

import blackbg from '../static/images/blackbg.png'
import consultants_thumbnail from '../static/images/consultants_thumbnail.png'
import developers_thumbnail from '../static/images/developers_thumbnail.png'
import financiers_thumbnail from '../static/images/financiers_thumbnail.png'
import integrators_thumbnail from '../static/images/integrators_thumbnail.png'
import utilities_thumbnail from '../static/images/utilities_thumbnail.png'
import { Import } from "lucide-react";

// Top banner remains unchanged
const TopBanner = () => {
  const navigate = useNavigate();

  const [activeEvent, setAtiveEvent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const getActiveEvent = () => {
      axios
        .get(process.env.REACT_APP_BASE_URL + "crafter/get-upcoming-event/")
        .then((response) => {
          const activeEvent = response.data;
          setAtiveEvent(activeEvent);
        })
        .catch((error) => {
          console.error("Error fetching active:", error);
        });
    };
    getActiveEvent();

    // Delay showing the banner by 2 seconds
    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 1250);

    return () => clearTimeout(timer); // Cleanup the timer
  }, []);

  const handleBannerClick = async () => {
    if (activeEvent.link.includes("ArticleDetails")) {
      // Extract the article ID from the link
      const id = activeEvent.link.split("ArticleDetails/")[1];

      try {
        // Fetch the article details by ID
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}crafter/knowledge-base-detail/${id}/`
        );
        const articleDetails = response.data;

        // Navigate to the ArticleDetails page with the article details
        navigate(`/ArticleDetails/${id}`, articleDetails);
      } catch (error) {
        console.error("Error fetching article details:", error);
      }
    } else {
      // Open the link in a new tab if it's not an article
      window.open(activeEvent.link, "_blank");
    }
  };

  return (
    showBanner &&
    activeEvent !== null && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <TopBannerContainer>
          <span className="banner-title">{activeEvent.title}</span>
          <button onClick={handleBannerClick} className="banner-button">
            {activeEvent.type}
          </button>
        </TopBannerContainer>
      </motion.div>
    )
  );
};

const AnimatedDropdown = ({ title, layout, children, active }) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const MenuWrapper = layout === "mega" ? MegaMenu : DropdownMenu;
  return (
    <AnimatedDropdownContainer
      $layout={layout}
      onMouseEnter={() => {
        if (window.innerWidth > 576) setOpen(true); // Only trigger on desktop
      }}
      onMouseLeave={() => {
        if (window.innerWidth > 576) setOpen(false); // Only trigger on desktop
      }}
    >
      <DropdownButton
        className={active ? "underline" : ""}
        onClick={handleToggle}
      >
        {title}
      </DropdownButton>
      <AnimatePresence>
        {open && (
          <MenuWrapper
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </MenuWrapper>
        )}
      </AnimatePresence>
    </AnimatedDropdownContainer>
  );
};

const FullWidthDropDownItem = ({ title, description, imgSrc, onClick }) => {
  return (
    <CardContainer onClick={onClick}>
      <ImageWrapper>
        <img src={imgSrc} alt={title} />
      </ImageWrapper>
      <ContentWrapper>
        <h4>{title}</h4>
        <p>{description}</p>
      </ContentWrapper>
    </CardContainer>
  );
}


// Main Header Component
const Header = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const logout = Logout();

  const session_is_auth = useSelector((state) => state.session.isLoggedIn);
  const [isAuth, setIsAuth] = useState(session_is_auth);

  useEffect(() => {
    setIsAuth(session_is_auth);
  }, [session_is_auth]);

  const redirectPage = (page) => {
    navigate(page);
  };

  const getActiveNav = (pathname) => {
    if (pathname.startsWith("/events") || pathname.startsWith("/knowledge_base")) return "EDUCATION";
    if (pathname.startsWith("/solutions")) return "SOLUTIONS";
    if (pathname === "/energy_storage_software") return "ES SOFTWARE";
    if (pathname === "/energy_storage_consulting") return "ES SERVICES";
    // if (pathname === "/knowledge_base") return "RESOURCES";
    if (pathname === "/about_us" || pathname === "/contact_us") return "ABOUT";
    if (pathname === "/shop/login") return "LOGIN";
    if (pathname === "/home") return "MY ACCOUNT";
    return "";
  };

  const activeNav = getActiveNav(location.pathname);

  const isSolutionsActive = location.pathname.startsWith("/solutions");
  const isEducationActive = activeNav === "EDUCATION";
  const isAboutActive = location.pathname === "/about_us" || location.pathname === "/contact_us";

  return (
    <>
      <TopBanner />
      <Navbar
        className="sticky-top navbar-dark"
        collapseOnSelect
        expand="lg"
        style={{ background: "#04ac4e" }}
      >
        <Navbar.Brand
          style={{ cursor: "pointer" }}
          onClick={() => redirectPage(isAuth ? "/home" : "/")}
        >
          <Row style={{ margin: "0px", padding: "0px" }}>
            <Col xs={12}>
              <img
                src={logo}
                width="300"
                height="80"
                className="d-inline-block align-top"
                alt="Logo"
              />
            </Col>
          </Row>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="nav_link_container ml-auto">
            {isAuth ? (
              <>
                <AnimatedDropdown title="SOLUTIONS" layout={"mega"} active={isSolutionsActive}>
                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/developers")}
                    imgSrc={developers_thumbnail}
                    title={"Developers & IPPs"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/utilities")}
                    imgSrc={utilities_thumbnail}
                    title={"Utilities"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/consultants")}
                    imgSrc={consultants_thumbnail}
                    title={"Consultants & IEs"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/integrators")}
                    imgSrc={integrators_thumbnail}
                    title={"Integrators"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/financiers")}
                    imgSrc={financiers_thumbnail}
                    title={"Financial Institutions"}
                    description={""}
                  />
                </AnimatedDropdown>

                <NavLink
                  className={activeNav === "ES SOFTWARE" ? "underline" : ""}
                  onClick={() => redirectPage("/energy_storage_software")}
                >
                  ES SOFTWARE
                </NavLink>
                <NavLink
                  className={activeNav === "ES SERVICES" ? "underline" : ""}
                  onClick={() => redirectPage("/energy_storage_consulting")}
                >
                  ES SERVICES
                </NavLink>

                <AnimatedDropdown title="EDUCATION" layout="list" active={isEducationActive}>
                  <DropdownItem onClick={() => redirectPage("/events")}>
                    EVENTS
                  </DropdownItem>
                  <DropdownItem onClick={() => redirectPage("/knowledge_base")}>
                    RESOURCES
                  </DropdownItem>
                </AnimatedDropdown>
                
                {/* <NavLink
                  className={activeNav === "RESOURCES" ? "underline" : ""}
                  onClick={() => redirectPage("/knowledge_base")}
                >
                  RESOURCES
                </NavLink> */}
                {/* Use AnimatedDropdown for unique, animated dropdown */}
                <AnimatedDropdown title="ABOUT" layout={"list"} active={isAboutActive}>
                  <DropdownItem onClick={() => redirectPage("/about_us")}>
                    WHO WE ARE
                  </DropdownItem>
                  <DropdownItem onClick={() => redirectPage("/contact_us")}>
                    CONTACT
                  </DropdownItem>
                </AnimatedDropdown>
                <NavLink
                  className={activeNav === "MY ACCOUNT" ? "underline" : ""}
                  onClick={() => redirectPage(session_is_auth ? "/home" : "/shop/login")}
                >
                  MY ACCOUNT
                </NavLink>
                <NavLink onClick={() => logout()}>LOGOUT</NavLink>
              </>
            ) : (
              <>
                <AnimatedDropdown title="SOLUTIONS" layout={"mega"} active={isSolutionsActive}>
                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/developers")}
                    imgSrc={developers_thumbnail}
                    title={"Developers & IPPs"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/utilities")}
                    imgSrc={utilities_thumbnail}
                    title={"Utilities"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/consultants")}
                    imgSrc={consultants_thumbnail}
                    title={"Consultants & IEs"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/integrators")}
                    imgSrc={integrators_thumbnail}
                    title={"Integrators"}
                    description={""}
                  />

                  <FullWidthDropDownItem
                    onClick={() => redirectPage("/solutions/financiers")}
                    imgSrc={financiers_thumbnail}
                    title={"Financial Institutions"}
                    description={""}
                  />
                </AnimatedDropdown>

                <NavLink
                  className={activeNav === "ES SOFTWARE" ? "underline" : ""}
                  onClick={() => redirectPage("/energy_storage_software")}
                >
                  ES SOFTWARE
                </NavLink>
                <NavLink
                  className={activeNav === "ES SERVICES" ? "underline" : ""}
                  onClick={() => redirectPage("/energy_storage_consulting")}
                >
                  ES SERVICES
                </NavLink>

                <AnimatedDropdown title="EDUCATION" layout="list" active={isEducationActive}>
                  <DropdownItem onClick={() => redirectPage("/events")}>
                    EVENTS
                  </DropdownItem>
                  <DropdownItem onClick={() => redirectPage("/knowledge_base")}>
                    RESOURCES
                  </DropdownItem>
                </AnimatedDropdown>
                {/* <NavLink
                  className={activeNav === "RESOURCES" ? "underline" : ""}
                  onClick={() => redirectPage("/knowledge_base")}
                >
                  RESOURCES
                </NavLink> */}
                {/* Use AnimatedDropdown for unique, animated dropdown */}
                <AnimatedDropdown title="ABOUT" layout={"list"} active={isAboutActive}>
                  <DropdownItem onClick={() => redirectPage("/about_us")}>
                    WHO WE ARE
                  </DropdownItem>
                  <DropdownItem onClick={() => redirectPage("/contact_us")}>
                    CONTACT
                  </DropdownItem>
                </AnimatedDropdown>
                <NavLink
                  className={activeNav === "LOGIN" ? "underline" : ""}
                  onClick={() => redirectPage("/shop/login")}
                >
                  LOGIN
                </NavLink>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  );
};

export default Header;

// Styled-components for Nav links & dropdown

const NavLink = styled(Nav.Link)`
  font-size: 0.95rem;
  font-weight: bold;
  cursor: pointer;
  color: white !important;
  padding: 0.5rem 1rem;
  border-bottom: 3px solid transparent;
  display: flex;
  align-items: center;
  height: 100%;

  &.underline {
    color: #edecec !important;
  }

  &:hover {
    color: #edecec !important;
  }
`;

const AnimatedDropdownContainer = styled.div`
  font-weight: bold;
  position: ${(props) => (props.$layout === "mega" ? "static" : "relative")} !important;
  display: flex;
  align-items: center;
  box-sizing: border-box;

  @media (min-width: 577px) {
    position: static;
  }

  @media (max-width: 576px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    margin: 0;
  }
`;

const DropdownButton = styled(Nav.Link)`
  font-size: 0.95rem;
  font-weight: bold;
  cursor: pointer;
  color: ${(props) => (props.$active ? "#edecec" : "white")} !important;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-decoration: none !important;
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    left: 1rem;
    right: 1rem;
    bottom: 2px;
    height: 3px;
    background: ${(props) => (props.$active ? "#edecec" : "transparent")};
    pointer-events: none;
  }

  &:hover {
    color: #edecec !important;
  }

  &:hover::after {
    background: #edecec;
  }

  @media (max-width: 576px) {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    padding: 0.5rem 1rem;
  }
`;

const DropdownMenu = styled(motion.ul)`
  position: absolute;
  top: 100%;
  left: 0;
  margin: 8px 0 0 0;
  padding: 8px 0;
  background: #04ac4e;
  border-radius: 4px;
  list-style: none;
  min-width: 150px;
  z-index: 1000;

  @media (max-width: 576px) {
    position: static; 
    margin: 0;
    padding: 0;
    background: transparent;
    box-shadow: none;
    width: 100%; 
    display: block; 
  }
`;

const DropdownItem = styled.li`
  font-size: 0.95rem;
  padding: 8px 16px;
  cursor: pointer;
  color: white;
  font-weight: bold;

  &:hover {
    background: #039944;
  }

  @media (max-width: 576px) {
    width: 100%; 
    text-align: left; 
    padding: 8px 12px; 
    box-sizing: border-box; 
  }
`;

// Styled-components for TopBanner
const TopBannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #00000010;
  padding: 10px 0;
  text-align: center;
  line-height: 1.6;
  height: 40px;

  .banner-title {
    margin-right: 20px;
    font-weight: 600;
    font-size: 1rem;
  }

  .banner-button {
    background-color: #04ac4e;
    border: none;
    color: white;
    font-weight: bold;
    padding: 6px 12px;
    cursor: pointer;
    border-radius: 4px;
    margin-left: 10px;
    margin-right: 10px;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    height: auto;
    padding: 8px;

    .banner-title {
      margin-right: 0;
      font-size: 0.9rem;
    }

    .banner-button {
      font-size: 0.85rem;
      padding: 4px 8px;
      margin: 5px 0;
    }
  }
`;
// A wider container for the dropdown to fit cards side-by-side
const MegaMenu = styled(motion.ul)`
  position: absolute;
  left: 0;
  right: 0;
  width: 100vw; 
  min-height: 200px; 
  height:auto;
  
  top: 82%; 
  margin: 0;
  padding: 30px 10%; 
  
  background: #04ac4e;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-content:flex-start;
  justify-content: center; 
  align-items: stretch;
  gap: 30px;
  z-index: 1000;
  box-shadow: 0px 10px 20px rgba(0,0,0,0.2);

  @media (max-width: 576px) {
    position: static;
    width: 100%;
    height: auto;
    flex-direction: column;
    padding: 10px;
  }
`;

const CardContainer = styled.div`
  display: flex;
  background: rgba(1, 122, 55, 0.5);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.3s ease;
  
  flex: 0 0 calc(20% - 30px); 
  max-width: 500px; 
  height: 10vw; 

  &:hover {
    background: rgba(1, 122, 55, 0.7);
    transform: translateY(-5px);   
  }

  @media (max-width: 1200px) {
     flex: 0 0 calc(33.33% - 20px); 
  }

  @media (max-width: 900px) {
     flex: 0 0 calc(50% - 20px); 
  }

  @media (max-width: 576px) {
    flex: 0 0 100%; 
    height: auto;
    flex-direction: column;
  }
`;

const ImageWrapper = styled.div`
  flex: 0 0 40%; 
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentWrapper = styled.div`
  flex: 0 0 60%; 
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h4 {
    font-size: 1rem;
    margin-bottom: 5px;
    color: white;
    font-weight: bold;
  }

  p {
    font-size: 0.85rem;
    margin: 0;
    color: #e0e0e0;
    line-height: 1.4;
  }
`;